/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module WafModule
 */

import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
} from '@angular/core';

import { ClrLoadingState } from '@cds/core/button';
import { ClrFormLayout } from '@clr/angular';
import { L10nService } from '@vmw/ngx-vip';
import { TWafPolicyCloneableItem } from 'ajs/modules/waf/waf.types';
import { WafPolicyCloneableItem } from 'ajs/modules/waf/factories/waf-policy/waf-policy.item.factory';
import * as l10n from './waf-policy-clone-dialog.l10n';
import './waf-policy-clone-dialog.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @author gprasadhk
 * @desc
 *     Dialog component allowing the user to clone WAF policy.
 */
@Component({
    selector: 'waf-policy-clone-dialog',
    templateUrl: './waf-policy-clone-dialog.component.html',
})
export class WafPolicyCloneDialogComponent implements OnInit {
    @Input()
    public wafPolicyId: string;

    /**
     * Name of the WAF policy to be cloned.
     */
    @Input()
    public wafPolicyName: string;

    /**
     * Called on dialog close.
     */
    @Output()
    public onClose = new EventEmitter();

    /**
     * Called on dialog submit.
     */
    @Output()
    public onSubmit = new EventEmitter();

    /**
     * Waf Policy Clone Name.
     */
    public wafPolicyCloneName: string;

    /**
     * Loading state of the submit button. Set to 'loading' when submitting the request.
     */
    public loadingState: ClrLoadingState = ClrLoadingState.default;

    /**
     * Error state of the component. Set to a backend error when the request to generate token
     * fails.
     */
    public error: string | null = null;

    /**
     * Layout for clrForm.
     */
    public readonly verticalLayout = ClrFormLayout.VERTICAL;

    public readonly l10nKeys = l10nKeys;

    constructor(
        l10nService: L10nService,
        @Inject(WafPolicyCloneableItem)
        private readonly WafPolicyCloneableItemClass: TWafPolicyCloneableItem,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Create a new cloned WAF Policy item and saves it.
     */
    public async cloneWafPolicy(): Promise<void> {
        try {
            this.loadingState = ClrLoadingState.loading;
            this.error = null;

            const { wafPolicyId: id, wafPolicyCloneName: name } = this;
            const wafPolicyCloneableitem = new this.WafPolicyCloneableItemClass({ id });

            await wafPolicyCloneableitem.load();
            await wafPolicyCloneableitem.clone(name).submit();

            this.onSubmit.emit();
        } catch ({ data }) {
            this.error = data?.error;
        } finally {
            this.loadingState = ClrLoadingState.default;
        }
    }

    /** @override */
    public ngOnInit(): void {
        this.wafPolicyCloneName = `${this.wafPolicyName} (copy)`;
    }

    /**
     * Called to close the dialog.
     */
    public handleClose(): void {
        this.onClose.emit();
    }
}
